/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 *
 * @module components/common/ErrorBoundary
 */

import React from 'react';
import { Alert, Button, Container } from 'react-bootstrap';
import { isChunkLoadError, reloadOnceForChunk } from '../../utils/chunkErrorHandler';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // React can swallow chunk-load errors before the window 'error' listener
    // sees them; handle that case here so the user gets a transparent reload
    // instead of the generic fallback after a stale-cache deploy.
    if (isChunkLoadError(error)) {
      reloadOnceForChunk();
      return;
    }
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container className="py-5">
          <Alert variant="danger">
            <Alert.Heading>
              <i className="bi bi-exclamation-triangle me-2"></i>
              Algo salió mal
            </Alert.Heading>
            <p>
              Lo sentimos, ha ocurrido un error inesperado. Por favor intenta de nuevo
              o contacta al soporte técnico si el problema persiste.
            </p>
            {this.state.error && (
              <details className="mt-3">
                <summary className="cursor-pointer">Detalles técnicos</summary>
                <pre className="mt-2 p-3 bg-light rounded" style={{ fontSize: '0.875rem' }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            <hr />
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={this.handleReset}>
                Intentar de nuevo
              </Button>
              <Button variant="outline-secondary" onClick={() => window.location.href = '/'}>
                Ir al inicio
              </Button>
            </div>
          </Alert>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
